import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import MapSnapshotButton from 'src/common/components/MapSnapshotButton';
import { toggleShowOnlySelectedBuildings } from '../pv-analysis.slice';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';

interface Properties {
    mapRef: RefObject<HTMLElement | null>;
    disabled: boolean;
    takeScreenshot: (blob: Blob) => void;
}

export const MAP_ASPECT_RATIO = 70 / 45;

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) =>
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Could not create a blob from the canvas'))),
            'image/png',
        ),
    );
}

/**
 * The PV Calculator's "Save map image": the shared {@link MapSnapshotButton}
 * with the PV specifics — only the selected buildings are shown during the
 * capture, the zoom control / attribution strip is trimmed off (right 50 px,
 * bottom at the 70:45 map aspect), and the photo leaves as a PNG blob that
 * rides along with the variant save.
 */
export default function Screenshot({ mapRef, disabled, takeScreenshot }: Properties) {
    const selectedBuildings = useAppSelector((state) => state.pvAnalysis.editedVariant.buildings);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const noBuildingSelected = selectedBuildings.length === 0;

    return (
        <MapSnapshotButton
            targetRef={mapRef}
            label={t('Save map image')}
            className="mapScreenshot"
            disabled={disabled || noBuildingSelected}
            disabledTooltip={
                noBuildingSelected ? t('You must select a building, before making a screenshot') : undefined
            }
            crop={{ right: 50, bottom: 50 / MAP_ASPECT_RATIO }}
            onBeforeCapture={() => dispatch(toggleShowOnlySelectedBuildings())}
            onAfterCapture={() => dispatch(toggleShowOnlySelectedBuildings())}
            onCapture={async (canvas) => {
                takeScreenshot(await canvasToPngBlob(canvas));
                return canvas.toDataURL('image/png');
            }}
        />
    );
}
